import { motion } from "framer-motion";
import { AnimatedWords, Reveal } from "@/components/motion";

const CHAPTERS = [
  {
    no: "01",
    title: "Who you train with matters",
    body: "Trainova was built on a simple idea. Most people struggle with consistency not because they lack discipline, but because they lack the right environment.",
  },
  {
    no: "02",
    title: "The right environment changes everything",
    body: "By connecting you with partners who match your goals, level, and energy, every workout becomes more focused, consistent, and effective.",
  },
  {
    no: "03",
    title: "Routines that actually last",
    body: "This isn't just about finding someone to train with. It's about building the kind of routine that sticks — long after motivation fades.",
  },
];

const STORY_IMG = "/story-treadmill.jpg";

export default function Story() {
  return (
    <section id="story" data-testid="story-section" className="relative bg-[#0A0A0A] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-16 flex items-center gap-3">
          <span className="h-px w-10 bg-teal" />
          <span className="font-condensed text-base tracking-[0.35em] text-teal">OUR STORY</span>
        </div>

        <div className="grid gap-16 lg:grid-cols-[0.9fr_1.1fr]">
          {/* Sticky left */}
          <div className="lg:sticky lg:top-28 lg:h-fit">
            <h2 className="font-display text-5xl leading-[0.9] uppercase sm:text-6xl md:text-7xl">
              <AnimatedWords text="Train with" />
              <br />
              <span className="accent-teal">
                <AnimatedWords text="intention" delay={0.2} />
              </span>
            </h2>
            <Reveal delay={0.3} className="mt-8">
              <div className="relative aspect-[4/5] max-w-sm overflow-hidden">
                <img src={STORY_IMG} alt="Black female athlete running on a treadmill" className="h-full w-full object-cover grayscale" />
                <div className="absolute inset-0 border border-white/10" />
                <div className="absolute bottom-0 left-0 bg-teal px-4 py-2 font-condensed text-lg tracking-widest text-black">
                  TRAIN BETTER. TOGETHER
                </div>
              </div>
            </Reveal>
          </div>

          {/* Scrolling chapters right */}
          <div className="flex flex-col gap-16">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.no}
                data-testid={`story-chapter-${c.no}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="border-t border-white/10 pt-8"
              >
                <div className="flex items-start gap-6">
                  <span className="font-display text-4xl text-teal/60 md:text-5xl">{c.no}</span>
                  <div>
                    <h3 className="font-condensed text-3xl tracking-wide md:text-4xl">{c.title}</h3>
                    <p className="mt-4 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
                      {c.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
